import { c as _c } from "react/compiler-runtime";
// Head-and-shoulders framing guide drawn over the live preview, in the style of
// clinical / ID-photo capture: an anatomically proportioned silhouette (eyes at
// half head height, ears from brow to nose base, neck ~60% of head width) plus a
// facial midline, an eye line and eye-centre targets to line the patient up
// against. Outside the silhouette is dimmed slightly so the target
// area stands out without washing out the face itself.
//
// Drawn with a fixed viewBox + "meet" so the outline keeps its shape (never
// stretches with the frame aspect) and stays centered. overflow="visible" lets
// the dimming and shoulders run into the letterbox area on portrait screens.
// vector-effect isn't inherited, so every stroked path sets it to stay sharp.
//
// The eye targets and viewBox live in face-align so the camera's alignment
// check aims at exactly what's drawn; the outline turns green once it passes.
import { FACE_GUIDE_EYES, FACE_GUIDE_VIEWBOX } from "@/lib/face-align";
import { cx } from "@/lib/tailwind-utils";
const HEAD = "M50 8 C61.8 8 70.8 15.6 71.9 28 C72.4 33.5 72.3 38 71.9 42 C71.5 48 70.4 53.5 68.4 57.8 C66.6 61.6 62.6 66.4 57.6 69 C55.2 70.1 52.6 70.4 50 70.4 C47.4 70.4 44.8 70.1 42.4 69 C37.4 66.4 33.4 61.6 31.6 57.8 C29.6 53.5 28.5 48 28.1 42 C27.7 38 27.6 33.5 28.1 28 C29.2 15.6 38.2 8 50 8 Z";
const EAR_RIGHT = "M71.9 38.5 C74.6 36.8 76.6 38.6 76.2 42.5 C75.8 46.5 74.6 50.8 70.2 52.6";
const EAR_LEFT = "M28.1 38.5 C25.4 36.8 23.4 38.6 23.8 42.5 C24.2 46.5 25.4 50.8 29.8 52.6";
const SHOULDER_LEFT = "M38.4 65.5 C38.6 70 38.2 74 36.8 77.4 C31.4 80.9 21.2 82.7 13.6 85.7 C7.6 88.1 4.4 92.6 3.4 100 L3 140";
const SHOULDER_RIGHT = "M61.6 65.5 C61.4 70 61.8 74 63.2 77.4 C68.6 80.9 78.8 82.7 86.4 85.7 C92.4 88.1 95.6 92.6 96.6 100 L97 140";
// Neck + shoulders as one closed shape, for the dimming mask only.
const BODY = "M38.4 62 L38.4 65.5 C38.6 70 38.2 74 36.8 77.4 C31.4 80.9 21.2 82.7 13.6 85.7 C7.6 88.1 4.4 92.6 3.4 100 L3 140 L97 140 L96.6 100 C95.6 92.6 92.4 88.1 86.4 85.7 C78.8 82.7 68.6 80.9 63.2 77.4 C61.8 74 61.4 70 61.6 65.5 L61.6 62 Z";
const MASK_ID = "face-guide-outside";
const VIEWBOX = `${FACE_GUIDE_VIEWBOX.x} ${FACE_GUIDE_VIEWBOX.y} ${FACE_GUIDE_VIEWBOX.width} ${FACE_GUIDE_VIEWBOX.height}`;
const EYE_LINE_Y = FACE_GUIDE_EYES.left.y;

// A small "+" centred on an eye target.
function crosshair({
  x,
  y
}: {
  x: number;
  y: number;
}) {
  return `M${x - 2.5} ${y} H${x + 2.5} M${x} ${y - 2.5} V${y + 2.5}`;
}
export default function FaceGuideOverlay(t0) {
  const $ = _c(10);
  const {
    aligned: t1
  } = t0;
  const aligned = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <defs><mask id={MASK_ID} maskUnits="userSpaceOnUse" x="-500" y="-500" width="1100" height="1100"><rect x="-500" y="-500" width="1100" height="1100" fill="white" /><path d={HEAD} fill="black" /><path d={BODY} fill="black" /></mask></defs>;
    t3 = <rect x="-500" y="-500" width="1100" height="1100" fill="black" fillOpacity={0.35} mask={`url(#${MASK_ID})`} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  const t4 = aligned ? "stroke-status-success-text" : "stroke-white";
  let t5;
  if ($[2] !== t4) {
    t5 = cx("transition-colors duration-300", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      filter: "drop-shadow(0 0 1.5px rgba(0,0,0,0.6))"
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <g strokeOpacity={0.9} strokeWidth={1.75}>{[HEAD, EAR_RIGHT, EAR_LEFT, SHOULDER_LEFT, SHOULDER_RIGHT].map(_temp)}</g>;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <g strokeOpacity={0.45} strokeWidth={1} strokeDasharray="5 5"><path d="M50 -15 V124" vectorEffect="non-scaling-stroke" /><path d={`M18 ${EYE_LINE_Y} H82`} vectorEffect="non-scaling-stroke" /></g>;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <g strokeOpacity={0.75} strokeWidth={1.5}><path d={crosshair(FACE_GUIDE_EYES.left)} vectorEffect="non-scaling-stroke" /><path d={crosshair(FACE_GUIDE_EYES.right)} vectorEffect="non-scaling-stroke" /></g>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t5) {
    t10 = <svg viewBox={VIEWBOX} preserveAspectRatio="xMidYMid meet" overflow="visible" aria-hidden={true} className="dark pointer-events-none absolute inset-0 z-10 h-full w-full">{t2}{t3}<g fill="none" className={t5} strokeLinecap="round" strokeLinejoin="round" style={t6}>{t7}{t8}{t9}</g></svg>;
    $[8] = t5;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  return t10;
}
function _temp(d) {
  return <path key={d} d={d} vectorEffect="non-scaling-stroke" />;
}